import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'

export default function NewBeneficiary() {
  const { id } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    relationship: '',
    beneficiary_type: 'current',
    interest_description: '',
    interest_start_date: '',
    interest_end_date: '',
    is_active: true,
    notes: ''
  })

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setFormData({ ...formData, [e.target.name]: value })
  }

  const handleSubmit = async () => {
    setError(null)

    if (!formData.full_name.trim()) {
      setError('Beneficiary name is required')
      return
    }

    setLoading(true)

    const { error: insertError } = await supabase
      .from('beneficiaries')
      .insert({
        trust_id: id,
        full_name: formData.full_name.trim(),
        email: formData.email || null,
        phone: formData.phone || null,
        relationship: formData.relationship || null,
        beneficiary_type: formData.beneficiary_type,
        interest_description: formData.interest_description || null,
        interest_start_date: formData.interest_start_date || null,
        interest_end_date: formData.interest_end_date || null,
        is_active: formData.is_active,
        notes: formData.notes || null
      })

    if (insertError) {
      setError(insertError.message)
      setLoading(false)
      return
    }

    await supabase.from('audit_log').insert({
      trust_id: id,
      user_id: user.id,
      action: 'created_beneficiary',
      table_affected: 'beneficiaries',
      new_value: formData
    })

    navigate(`/trusts/${id}?tab=beneficiaries`)
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(`/trusts/${id}`)}
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            ← Back
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add Beneficiary</h2>
            <p className="text-sm text-gray-500">Add a current or residual beneficiary to this trust</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Jane Smith"
            />
          </div>

          {/* Beneficiary Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beneficiary Type <span className="text-red-500">*</span>
            </label>
            <select
              name="beneficiary_type"
              value={formData.beneficiary_type}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="current">Current — entitled to current distributions</option>
              <option value="residual">Residual — eligible for future or remainder interest</option>
              <option value="both">Both — current and residual interest</option>
            </select>
          </div>

          {/* Interest Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Interest Description
            </label>
            <textarea
              name="interest_description"
              value={formData.interest_description}
              onChange={handleChange}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Income beneficiary for life; remainder to descendants per stirpes"
            />
            <p className="text-xs text-gray-400 mt-1">
              Describe the nature of this beneficiary's interest as defined in the trust instrument
            </p>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="jane@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="(616) 555-0100"
              />
            </div>
          </div>

          {/* Relationship */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Relationship to Grantor <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              name="relationship"
              value={formData.relationship}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Spouse, Child, Grandchild"
            />
          </div>

          {/* Interest Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Interest Start Date <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="date"
                name="interest_start_date"
                value={formData.interest_start_date}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Interest End Date <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="date"
                name="interest_end_date"
                value={formData.interest_end_date}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-3 py-2">
            <input
              type="checkbox"
              name="is_active"
              id="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
              Interest is currently active
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Any additional notes..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Adding beneficiary...' : 'Add Beneficiary'}
            </button>
            <button
              onClick={() => navigate(`/trusts/${id}`)}
              className="px-6 border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium py-2.5 rounded-lg transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Layout>
  )
}